"use client";

import { Spin, Alert } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import usePatientForgotPassword from "../../hooks/usePatientForgotPassword";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "../../../authPatient/pages/SignupPatient/SignupPatient.css";
import "./ForgotPassword.css";

const FormInput = ({
  label,
  type = "text",
  placeholder = "",
  value,
  onChange,
  error,
  name,
  autoComplete,
}) => {
  return (
    <div className="signup-form-group">
      {label && (
        <label className="signup-form-label" htmlFor={name}>
          {label}
        </label>
      )}
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={onChange}
        className={`signup-form-input${error ? " signup-password-mismatch" : ""}`}
        autoComplete={autoComplete}
      />
      {error ? <span className="fpw-error-text">{error}</span> : null}
    </div>
  );
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const {
    email,
    setEmail,
    error,
    loading,
    handleSendCode,
    onKeyDown,
    goLogin,
  } = usePatientForgotPassword();

  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");

  return (
    <div className={`signup-container signup-patient-auth fpw-page ${isRTL ? "is-rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      <button
        type="button"
        className="signup-back-home signup-back-home-patient"
        onClick={() => navigate(ROUTES.LANDING)}
        aria-label={t("common.backToHome", "Back to home page")}
      >
        <ArrowLeftOutlined />
        <span>{t("common.backToHome", "Back to home page")}</span>
      </button>

      <div className="signup-background signup-doctor">
        <section className="signup-form-wrapper">
          <div className="signup-form" onKeyDown={onKeyDown}>
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("auth.forgotPassword", "Forgot Password").split(" ").map((word, idx, arr) => 
                  word.toLowerCase() === "password" ? 
                    <span key={idx} style={{ color: "rgba(15, 66, 177, 1) !important" }}>{word}</span> : 
                    <span key={idx}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                )}
              </h1>
              <p className="signup-form-subtitle">{t("auth.forgotPasswordSubtitle")}</p>
            </div>

            <div className="signup-form-content">
              {loading && (
                <div className="fpw-loader">
                  <Spin tip={t("forgetPassword.loading", "Loading...")} />
                </div>
              )}

              {error && (
                <Alert
                  className="fpw-alert"
                  type="error"
                  message={error}
                  showIcon
                />
              )}

              <FormInput
                name="email"
                label={t("commonn.email")}
                type="email"
                placeholder={t("commonn.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                error={error}
              />

              <div className="signup-form-group">
                <button
                  type="button"
                  className="signup-create-account-btn"
                  disabled={!email || loading}
                  onClick={handleSendCode}
                >
                  {loading ? t("auth.sendingCode", "Sending...") : t("auth.sendCode")}
                </button>

                <div className="signup-login-link-wrapper">
                  <button
                    type="button"
                    className="signup-login-link"
                    onClick={goLogin}
                  >
                    {t("auth.backToLogin")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="signup-hero" style={{ backgroundImage: `url(${chooseImage})` }}>
          <div className="fpw-overlay" />
          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>
          <div className="signup-hero-inner">
            <h3 className="signup-hero-title">
              {t("signup.heroPanelTitle", "Are you joining as a healthcare professional?")}
            </h3>
            <p className="signup-hero-sub">
              {t("signup.heroPanelText", "This registration is designed for patients. If you are a doctor and wish to manage your practice, schedule appointments, and connect with patients, please create a doctor account.")}
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={() => navigate("/LoginDoctor")}
            >
              <span>{t("signup.continueAsDoctor", "Continue as a doctor")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ForgotPassword;
